import React from "react";
import { Button, ProgressBar, Table } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { BetatesterUploadAction, BetatesterUploadState } from "@/const";
import { useRequest } from "@components/AppHook";
import { ExportButton } from "@components/Common/Button";
import { RenderForm } from "@components/Forms/Render";
import { Awesome } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function Betatesters(props) {
    const params = useParams();
    if (params.action) {
        props = { ...props, action: params.action };
        return <Action {...props} />;
    }
    return <List {...props} />;
}

function List({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:game:betatester:list", { game_id: gameId }));
    const { setDelData } = useAppContext();

    if (!apiData) return;

    const headerTools = apiData.ext_system_id && (
        <>
            <ExportButton size="sm" />{" "}
            <Link to={url_reverse("games:betatester:del", { game_id: gameId })} className="btn btn-sm btn-dark">
                <Awesome type="minus-circle" /> {t("Удалить")}
            </Link>{" "}
            <Link to={url_reverse("games:betatester:add", { game_id: gameId })} className="btn btn-sm btn-dark">
                <Awesome type="plus-circle" /> {t("Добавить")}
            </Link>
        </>
    );

    const uploadProcent = () => {
        if (!apiData.btu.processed_cnt) {
            return 0;
        }
        return Math.round((100 * apiData.btu.processed_cnt) / apiData.btu.emails_cnt);
    };

    const getError = (errors) => {
        return Object.entries(errors).reduce((acc, [key, value]) => {
            if (acc[value]) {
                acc[value].push(`${key}`);
            } else {
                acc[value] = [key];
            }
            return acc;
        }, {});
    };

    const getDescription = () => {
        let resultText = `${apiData.btu.date_added} - ${apiData.btu.action}. ${t("Всего логинов")}: ${
            apiData.btu.emails_cnt
        }. `;

        if (apiData.btu.data?.login_to_action) {
            if (apiData.btu.action === BetatesterUploadAction.DEL) {
                resultText += `${t("Удалено")}: ${apiData.btu.data.login_to_action}. `;
            } else {
                resultText += `${t("Добавлено")}: ${apiData.btu.data.login_to_action}. `;
            }
        } else {
            resultText += `${"Ни один логин не был обработан"}. `;
        }
        return (
            <>
                {resultText}
                {t("Статус")}: <b>{apiData.btu.state}</b>
            </>
        );
    };
    return (
        <ListingCard header={header} headerTools={headerTools}>
            {apiData.ext_system_id ? (
                <>
                    {apiData.btu && (
                        <div className="dmg-callout dmg-callout-secondary">
                            {apiData.btu.state === BetatesterUploadState.WORK && (
                                <ProgressBar now={uploadProcent()} label={`${uploadProcent()}%`} />
                            )}

                            <div className="d-flex justify-content-between">
                                <div>
                                    <small>
                                        {getDescription()}
                                        {apiData.btu.data?.errors &&
                                            Object.entries(getError(apiData.btu.data?.errors)).map(
                                                ([error, logins]) => (
                                                    <div key={`err_${error}`} className="text-danger">
                                                        {error}: <br />
                                                        {Array.isArray(logins)
                                                            ? logins.map((email, index) => (
                                                                  <React.Fragment key={index}>
                                                                      <div className="text-dark">
                                                                          {email}
                                                                          <br />
                                                                      </div>
                                                                  </React.Fragment>
                                                              ))
                                                            : null}
                                                    </div>
                                                )
                                            )}
                                    </small>
                                </div>
                                <Awesome
                                    type="times"
                                    color="black"
                                    onClick={() =>
                                        setDelData({
                                            url: url_reverse("api:game:betatester:upload_delete", {
                                                game_id: gameId,
                                                upload_id: apiData.btu.id,
                                            }),
                                            afterRespOK: () => {
                                                const newApiData = { ...apiData };
                                                delete newApiData.btu;
                                                setApiData(newApiData);
                                            },
                                        })
                                    }
                                />
                            </div>
                        </div>
                    )}

                    <Table hover responsive className="dmg-table">
                        <thead>
                            <tr>
                                <th>{t("Логин")}</th>
                                <th>{t("Описание")}</th>
                                <th style={{ width: "6%" }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {apiData.object_list?.map((o, i) => {
                                return (
                                    <tr key={`object_list${i}`}>
                                        <td>{o.email}</td>
                                        <td>{o.__bt_descr}</td>
                                        <td className="dmg-table-row-tools">
                                            <Awesome
                                                type="trash-o"
                                                color="red"
                                                onClick={() =>
                                                    setDelData({
                                                        url: url_reverse(
                                                            "api:game:betatester:list",
                                                            { game_id: gameId },
                                                            { ids: [o.id] }
                                                        ),
                                                    })
                                                }
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </>
            ) : (
                <p className="text-danger">
                    <Trans>Бета-тестирование используется для массовой проверки силами пользователей.</Trans>
                    <br />
                    <Trans>
                        Включение режима позволяет добавлять бета-тестеров списком в текстовом и табличном виде.{" "}
                        <a href="https://documentation.vkplay.ru/p2p_vkp/p2p_betatesting">Подробнее</a>.
                    </Trans>
                    <br />
                    <Trans>
                        Для выдачи доступа тестировщикам воспользуйтесь разделом{" "}
                        <Link to={url_reverse("games:account:list", { game_id: gameId })}>«Аккаунты»</Link>.
                    </Trans>
                    <br />
                    <Trans>
                        Для включения режима бета-тестирования обратитесь к сотруднику платформы или напишите на
                        integration@vk.team.
                    </Trans>
                    <br />
                </p>
            )}
        </ListingCard>
    );
}

function Action({ action, gameId, header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:betatester:upload", { game_id: gameId, action }));
    const show_button = apiData?.show_button;
    const { api_post } = useRequest();
    const navigate = useNavigate();

    if (!apiData) return;

    const cancelUploadHandle = (ev) => {
        api_post(ev, url_reverse("api:game:betatester:list", { game_id: gameId }), {
            params: { action: "cancel_upload" },
            then: (resp) => {
                if (resp.status === "ok") {
                    navigate(url_reverse("games:betatester:list", { game_id: gameId }));
                }
            },
        });
    };

    return (
        <CommonCard header={header}>
            {show_button && (
                <Button className="mb-3" name="cancel_upload" onClick={cancelUploadHandle}>
                    {t("Отменить текущую загрузку")}
                </Button>
            )}
            <div className="dmg-callout dmg-callout-info mb-3">
                <div>{t("Для добавления пользователя по user_id используйте формат")}: user_id:123123</div>
            </div>
            <RenderForm backUrl={url_reverse("games:betatester:list", { game_id: gameId })} />
        </CommonCard>
    );
}
